import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag } from "antd"
import type React from "react"
import { UserCellLink } from "@/components/UserCellLink"
import { useTableScroll } from "@/hooks/useTableScroll"
import type { PointTransactionItem } from "@/services/gamification/pointTransactionApi"
import "@/styles/table.full.css"
import dayjs from "dayjs"

interface PointTransactionTableProps {
  data: PointTransactionItem[]
  loading: boolean
  pagination?: TableProps["pagination"]
}

export const PointTransactionTable: React.FC<PointTransactionTableProps> = ({ data, loading, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const getEventTag = (eventType: string) => {
    switch (eventType) {
      case "srs_study_completed":
        return <Tag color="blue">Ôn tập SRS</Tag>
      case "streak_reward":
        return <Tag color="orange">Thưởng chuỗi</Tag>
      case "set_completed":
        return <Tag color="green">Hoàn thành bộ từ</Tag>
      case "level_up":
        return <Tag color="purple">Thăng cấp</Tag>
      case "admin_adjustment":
        return <Tag color="magenta">Admin điều chỉnh</Tag>
      default:
        return <Tag color="default">{eventType}</Tag>
    }
  }

  const columns: TableProps["columns"] = [
    {
      title: "#ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 70,
      align: "center",
      render: (value: number) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Người dùng",
      key: "user",
      width: 240,
      fixed: "left",
      render: (_, record: PointTransactionItem) => (
        <UserCellLink user={record.user} userId={record.user_id} />
      ),
    },
    {
      title: "Biến động điểm",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "center",
      render: (amount: number) => {
        if (amount > 0) {
          return (
            <Tag color="success" icon={<ArrowUpOutlined />} className="px-2 py-0.5 font-semibold text-xs">
              +{amount.toLocaleString()} pts
            </Tag>
          )
        }
        if (amount < 0) {
          return (
            <Tag color="error" icon={<ArrowDownOutlined />} className="px-2 py-0.5 font-semibold text-xs">
              {amount.toLocaleString()} pts
            </Tag>
          )
        }
        return <Tag color="default">0 pts</Tag>
      },
    },
    {
      title: "Số dư sau biến động",
      dataIndex: "balance_after",
      key: "balance_after",
      width: 150,
      align: "center",
      render: (balance: number) => (
        <span className="font-bold font-mono text-amber-600">
          {balance !== undefined && balance !== null ? `${balance.toLocaleString()} pts` : "—"}
        </span>
      ),
    },
    {
      title: "Sự kiện",
      dataIndex: "event_type",
      key: "event_type",
      width: 160,
      align: "center",
      render: (eventType: string) => getEventTag(eventType),
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      align: "center",
      render: (val: string) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm:ss") : "—"),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

PointTransactionTable.displayName = "PointTransactionTable"

export default PointTransactionTable
